import CircularProgress from "@mui/material/CircularProgress";
import isNil from "lodash/isNil";
import { useEffect, useMemo } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import { useDrilldownBaseUrl, useIsAcquisitions } from "../../../../pipelines/PipelineContext";
import { loanFetchedDataLoanTermsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { DrilldownRouterSubTabStrip } from "../../components/DrilldownSubTabStrip";
import { getDrilldownSubTabs } from "../../components/drilldownSubTabs";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import LoanClosingSubTabClosingDocs from "./LoanClosingSubTabClosingDocs";
import LoanClosingSubTabReadiness from "./LoanClosingSubTabReadiness";
import LoanClosingSubTabSettlementWorkbook from "./LoanClosingSubTabSettlementWorkbook";

// The Closing tab is the closing-readiness surface (closing-readiness.md): the
// readiness signal + the closing-documents gate. The older Wire & Funding /
// Escrow sub-tabs were removed — wire reconciliation lives on its own top-level
// Wire & Funding tab.
const SUB_TABS = {
  READINESS: "readiness",
  CLOSING_DOCS: "closing-documents",
  SETTLEMENT_WORKBOOK: "settlement-workbook",
};

// Readiness is the point of the tab (closing-readiness.md §3) — it lands first.
const DEFAULT_SUB_TAB = SUB_TABS.READINESS;

// The Conditions checklist card's width (LoanDrilldownConditionsChecklistTab) —
// the Closing tab's content shares its column.
const CONTENT_MAX_WIDTH = 1180;

const LoanDrilldownClosingTab = () => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const baseUrl = useDrilldownBaseUrl(loanDrilldown?._id);
  const isAcquisitions = useIsAcquisitions();
  const { getLoanTerms } = useLoanTermsHook();
  // The settlement workbook is three panes (PDF · lines · balance) and needs
  // the full width; every other sub-view keeps the Conditions-card column.
  const { pathname } = useLocation();
  const wide = pathname.includes(`/${SUB_TABS.SETTLEMENT_WORKBOOK}`);

  useEffect(() => {
    if (isNil(loanTerms) && loanDrilldown?._id) {
      getLoanTerms({
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => dispatch(loanFetchedDataLoanTermsSet(data)),
        onFailFn: () => dispatch(loanFetchedDataLoanTermsSet({})),
      });
    }
  }, [loanTerms, loanDrilldown?._id]);

  // The sub-views (labels + icons) come from the drilldown sub-tab registry —
  // the same list the primary nav's flyout offers — so the two never drift.
  // Labels branch on the pipeline: an acquisition schedules a TRADE
  // SETTLEMENT, not a closing, and the documents that gate it are the seller's
  // executed set.
  const subTabs = useMemo(
    () =>
      (getDrilldownSubTabs("closing", { isAcquisitions }) || []).map((o) => ({
        ...o,
        to: `${baseUrl}/${o.to}`,
      })),
    [baseUrl, isAcquisitions],
  );

  return (
    <LoanDrilldownTabPanel>
      {/* No page title — the sub-view strip is the header, the SAME strip the
          Documents tab opens with (DrilldownSubTabStrip). The pipeline
          (Closing / Settlement) is already named by the primary nav tab. */}
      <DrilldownRouterSubTabStrip options={subTabs} />

      {/* Every sub-view (Readiness / Closing Documents, and the Settlement
          variants on acquisitions) sits in the same column as the Conditions
          checklist card, so the two tabs line up. */}
      {isNil(loanTerms) ? (
        <Flx center sx={{ p: 7 }}>
          <CircularProgress />
        </Flx>
      ) : (
        <ScreenContent
          sx={{
            flexGrow: 1,
            width: "100%",
            maxWidth: wide ? "none" : CONTENT_MAX_WIDTH,
            margin: "0px auto",
          }}
        >
          <Routes>
            <Route index element={<Navigate to={DEFAULT_SUB_TAB} replace />} />
            <Route path={SUB_TABS.READINESS} element={<LoanClosingSubTabReadiness />} />
            <Route path={SUB_TABS.CLOSING_DOCS} element={<LoanClosingSubTabClosingDocs />} />
            {!isAcquisitions ? (
              <Route
                path={SUB_TABS.SETTLEMENT_WORKBOOK}
                element={<LoanClosingSubTabSettlementWorkbook />}
              />
            ) : null}
            {/* Any old deep link (wire-and-funding, escrow, wire-instructions,
                funding-shield, …) resolves to Readiness so bookmarks don't 404. */}
            <Route
              path="*"
              element={<Navigate to={DEFAULT_SUB_TAB} replace />}
            />
          </Routes>
        </ScreenContent>
      )}
    </LoanDrilldownTabPanel>
  );
};

export default LoanDrilldownClosingTab;
